import {isSupplementaryFormComplete} from '@sollapay/domain/rules';
import {DEFAULT_LINK_EXPIRY_DURATION} from '@sollapay/enums';
import {parseMoney} from '@sollapay/utils';
import {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {config} from '@/app/app.config';
import {
  useBuyerSubTabNavigation,
  useCreateBuyerForm,
  useCreatePaymentInstructionMutation,
  usePaymentInitiationMethod,
  usePaymentTransferType,
  usePurchaseBuyers
} from '@/features/buyers/hooks';
import {
  formatPurchaseAssetNames,
  hasSupplementaryInput,
  isBuyerFlowNextDisabled,
  isPaymentNextDisabled,
  isPaymentRequestDirty,
  toBuyerOption
} from '@/features/buyers/utils';
import {useTrustTypeStrategy} from '@/features/trust-accounts/providers';

import {BuyersModal} from './BuyersModal';
import {CreateBuyerStepsNav} from './BuyersModal/create-buyer/CreateBuyerStepsNav';
import {CreateBuyerTabContent} from './BuyersModal/create-buyer/CreateBuyerTabContent';
import {PaymentRequestTabContent} from './BuyersModal/payment-request/PaymentRequestTabContent';
import {BuyerSuccessPromptModal} from './BuyerSuccessPromptModal';
import {PaymentSentModal} from './PaymentSentModal';

import type {SubTab} from './BuyersModal/create-buyer/CreateBuyerTabContent';
import type {LinkExpiryDuration} from '@sollapay/enums';
import type {BuyerDTO, FatcaCrsSupplementaryPayload} from '@sollapay/types';
import type {FC, ReactNode} from 'react';

type FlowStep = 'idle' | 'add-buyer' | 'success-prompt' | 'payment-request' | 'payment-sent';

export interface AddBuyerFlowProps {
  trustAccountId: string;
  trigger: (onOpen: () => void) => ReactNode;
}

export const AddBuyerFlow: FC<AddBuyerFlowProps> = ({trustAccountId, trigger}) => {
  const {t} = useTranslation('trustAccounts');
  const strategy = useTrustTypeStrategy();
  const [flowStep, setFlowStep] = useState<FlowStep>('idle');
  const [createdBuyer, setCreatedBuyer] = useState<BuyerDTO | null>(null);
  const [expiryDuration, setExpiryDuration] = useState<LinkExpiryDuration>(
    DEFAULT_LINK_EXPIRY_DURATION
  );
  const [idempotencyKey, setIdempotencyKey] = useState<string>('');
  const [uploadedDocumentIds, setUploadedDocumentIds] = useState<string[]>([]);
  // Tags every document uploaded in this "Add Buyer" session so display and AI extraction
  // never pick up a leftover document from a previous (abandoned or completed) attempt.
  const [uploadSessionId, setUploadSessionId] = useState('');
  const [customAmountNis, setCustomAmountNis] = useState('');
  const [selectedBuyerId, setSelectedBuyerId] = useState('');
  const [supplementaryValues, setSupplementaryValues] =
    useState<FatcaCrsSupplementaryPayload | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('buyer-details');

  const {buyers: purchaseBuyers} = usePurchaseBuyers(
    trustAccountId,
    createdBuyer?.purchases[0]?.id,
    createdBuyer?.id
  );
  const paymentBuyerOptions =
    purchaseBuyers.length > 0
      ? purchaseBuyers.map(toBuyerOption)
      : createdBuyer
        ? [toBuyerOption(createdBuyer)]
        : [];
  const effectiveBuyerId = selectedBuyerId || (createdBuyer?.id ?? '');
  const selectedBuyerForPayment =
    purchaseBuyers.find(b => b.id === effectiveBuyerId) ?? createdBuyer;

  const {
    form,
    buyerFields,
    appendBuyer,
    removeBuyer,
    replaceBuyers,
    handleSave,
    handleCancel: handleCancelWithAI,
    saveError,
    isSaving,
    aiFields,
    isProcessing,
    suggestionsCount,
    rowAi,
    markFieldModified,
    handleFillManually,
    reindexBuyerAiFields
  } = useCreateBuyerForm({
    trustAccountId,
    uploadedDocumentIds,
    uploadSessionId,
    supplementaryValues,
    onSuccess: (buyer: BuyerDTO) => {
      setCreatedBuyer(buyer);
      setFlowStep('success-prompt');
    }
  });

  const createPaymentMutation = useCreatePaymentInstructionMutation(trustAccountId);

  const maxAmountNis = createdBuyer?.purchases[0]
    ? parseMoney(createdBuyer.purchases[0].purchasePriceNis)
        .percentOf(strategy.maxCollectionPercentage)
        .toString()
    : '0';

  // New buyer has no prior payments, so remaining = max
  const amount = config.features.allowCustomPaymentAmount ? customAmountNis : maxAmountNis;
  const {transferType, isZahavOnly, setTransferType, resetTransferType} =
    usePaymentTransferType(amount);
  const {initiationMethod, selectedInitiationMethod, setInitiationMethod, resetInitiationMethod} =
    usePaymentInitiationMethod(selectedBuyerForPayment?.partyType);

  const resetFlow = useCallback(() => {
    setFlowStep('idle');
    setCreatedBuyer(null);
    resetInitiationMethod();
    resetTransferType();
    setExpiryDuration(DEFAULT_LINK_EXPIRY_DURATION);
    setIdempotencyKey('');
    setUploadedDocumentIds([]);
    setUploadSessionId('');
    setCustomAmountNis('');
    setSelectedBuyerId('');
    setSupplementaryValues(null);
    setActiveSubTab('buyer-details');
    handleCancelWithAI();
  }, [handleCancelWithAI, resetTransferType, resetInitiationMethod]);

  const handleOpenAddBuyer = useCallback(() => {
    setUploadSessionId(crypto.randomUUID());
    setFlowStep('add-buyer');
  }, []);

  const handleContinueToPayment = () => {
    setIdempotencyKey(crypto.randomUUID());
    setSelectedBuyerId(createdBuyer?.id ?? '');
    setFlowStep('payment-request');
  };

  const handleSendPaymentRequest = async () => {
    const purchase = createdBuyer?.purchases[0];
    if (!purchase || !initiationMethod || !idempotencyKey) return;

    try {
      await createPaymentMutation.mutateAsync({
        trustBuyerPurchaseId: purchase.id,
        buyerPartyId: effectiveBuyerId,
        amount,
        initiationMethod,
        transferType,
        idempotencyKey,
        expiryDuration
      });
      setFlowStep('payment-sent');
    } catch {
      // Error surfaced via createPaymentMutation.error → saveError prop
      // Regenerate key so a retry doesn't reuse a potentially consumed key
      setIdempotencyKey(crypto.randomUUID());
    }
  };

  const isBuyerFormValid = form.formState.isValid && buyerFields.length > 0;

  const {handleContinue, handleSubTabChange} = useBuyerSubTabNavigation(form, setActiveSubTab);

  const isSupplementarySigned =
    supplementaryValues !== null &&
    isSupplementaryFormComplete(supplementaryValues.declarationValues);

  // New buyer: remaining = max (no prior payments)
  const isNextDisabled = isPaymentNextDisabled({
    initiationMethod,
    allowCustomAmount: config.features.allowCustomPaymentAmount,
    customAmountNis,
    remainingAmountNis: maxAmountNis,
    enforcesCollectionCap: strategy.enforcesCollectionCap
  });

  return (
    <>
      {trigger(handleOpenAddBuyer)}

      {/* Screen 1 — Add Buyer Modal */}
      <BuyersModal
        isOpen={flowStep === 'add-buyer'}
        isFlushBody={activeSubTab === 'buyer-details'}
        onOpenChange={open => {
          if (!open && flowStep === 'add-buyer') resetFlow();
        }}
        isDirty={
          form.formState.isDirty ||
          uploadedDocumentIds.length > 0 ||
          hasSupplementaryInput(supplementaryValues)
        }
        discardDescription={t('view.buyers.addBuyerModal.discardDescription')}
        backClosesModal={activeSubTab === 'buyer-details'}
        activeTab="create-buyer"
        isBuyerCreated={false}
        nextLabel={
          activeSubTab === 'buyer-details'
            ? t('view.buyers.addBuyerModal.footer.continue')
            : t('view.buyers.addBuyerModal.footer.addBuyer')
        }
        nextDisabled={isBuyerFlowNextDisabled({
          isDetailsStep: activeSubTab === 'buyer-details',
          buyerCount: buyerFields.length,
          isSupplementarySigned
        })}
        nextIsLoading={isSaving}
        backLabel={t('view.buyers.addBuyerModal.footer.back')}
        onNextClick={activeSubTab === 'buyer-details' ? handleContinue : handleSave}
        onBackClick={() => setActiveSubTab('buyer-details')}
        subHeader={
          <CreateBuyerStepsNav
            activeSubTab={activeSubTab}
            onSubTabChange={handleSubTabChange}
            isBuyerFormValid={isBuyerFormValid}
          />
        }
      >
        <CreateBuyerTabContent
          trustAccountId={trustAccountId}
          uploadSessionId={uploadSessionId}
          form={form}
          buyerFields={buyerFields}
          appendBuyer={appendBuyer}
          removeBuyer={removeBuyer}
          replaceBuyers={replaceBuyers}
          onDocumentsChange={setUploadedDocumentIds}
          saveError={saveError}
          aiFields={aiFields}
          isProcessing={isProcessing}
          suggestionsCount={suggestionsCount}
          rowAi={rowAi}
          markFieldModified={markFieldModified}
          onFillManually={handleFillManually}
          reindexBuyerAiFields={reindexBuyerAiFields}
          onSupplementaryValuesChange={setSupplementaryValues}
          activeSubTab={activeSubTab}
        />
      </BuyersModal>

      {/* Screen 2 — Success Prompt */}
      <BuyerSuccessPromptModal
        isOpen={flowStep === 'success-prompt'}
        buyerName={createdBuyer?.fullName ?? ''}
        onContinueToPayment={handleContinueToPayment}
        onBackToBuyers={resetFlow}
      />

      {/* Screen 3 — Payment Request Modal */}
      <BuyersModal
        isOpen={flowStep === 'payment-request'}
        onOpenChange={open => {
          if (!open && flowStep === 'payment-request') resetFlow();
        }}
        isDirty={isPaymentRequestDirty({
          initiationMethod: selectedInitiationMethod,
          customAmountNis,
          expiryDuration,
          selectedBuyerId,
          defaultBuyerId: createdBuyer?.id
        })}
        activeTab="payment-request"
        isBuyerCreated={true}
        nextLabel={t('view.buyers.addBuyerModal.footer.sendPaymentRequest')}
        nextDisabled={isNextDisabled}
        backLabel={t('view.buyers.addBuyerModal.footer.back')}
        nextIsLoading={createPaymentMutation.isPending}
        onNextClick={handleSendPaymentRequest}
        onBackClick={resetFlow}
      >
        <PaymentRequestTabContent
          amount={amount}
          onAmountChange={setCustomAmountNis}
          maxAmountNis={maxAmountNis}
          remainingAmountNis={maxAmountNis}
          assetLabel={
            createdBuyer?.purchases[0]
              ? formatPurchaseAssetNames(createdBuyer.purchases[0].assets, t)
              : undefined
          }
          buyers={paymentBuyerOptions}
          selectedBuyerId={effectiveBuyerId}
          onBuyerChange={setSelectedBuyerId}
          initiationMethod={initiationMethod}
          onInitiationMethodChange={setInitiationMethod}
          transferType={transferType}
          onTransferTypeChange={setTransferType}
          isZahavOnly={isZahavOnly}
          expiryDuration={expiryDuration}
          onExpiryDurationChange={setExpiryDuration}
          saveError={createPaymentMutation.error}
        />
      </BuyersModal>

      {/* Payment Sent Confirmation */}
      {createdBuyer?.purchases[0] && initiationMethod && (
        <PaymentSentModal
          isOpen={flowStep === 'payment-sent'}
          buyerName={selectedBuyerForPayment?.fullName ?? ''}
          assetLabel={formatPurchaseAssetNames(createdBuyer.purchases[0].assets, t)}
          purchasePriceNis={createdBuyer.purchases[0].purchasePriceNis}
          initiationMethod={initiationMethod}
          amount={amount}
          onClose={resetFlow}
        />
      )}
    </>
  );
};
